import React, { useState, useEffect } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useTenantConfig } from '@/hooks/useTenantConfig';
import { useModulePermissions } from '@/hooks/useModulePermissions';
import { useDashboardStats } from '@/hooks/useDashboardStats';
import { RoleBasedAccessSummary } from '@/components/dashboard/RoleBasedAccessSummary';
import { BusinessIntelligencePanel } from '@/components/dashboard/BusinessIntelligencePanel';
import { SalesIndicators } from '@/components/dashboard/SalesIndicators';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { KPICard } from '@/components/ui/kpi-card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Building2, 
  Users, 
  ShoppingCart, 
  Wallet, 
  TrendingUp, 
  TrendingDown,
  Activity,
  Package,
  Clock,
  AlertCircle,
  LogOut,
  FileBarChart,
  Settings,
  UserCheck,
  Box,
  Store,
  RefreshCw
} from 'lucide-react';
import { formatCurrency, formatPercentage } from '@/lib/number-formatting';

interface DashboardStats {
  totalSales: number;
  salesGrowth: number;
  totalOrders: number;
  ordersGrowth: number;
  totalProducts: number;
  lowStock: number;
  totalUsers: number;
  activeUsers: number;
  // Nuevos campos para órdenes con anticipo
  advanceOrders: number;
  pendingAdvanceOrders: number;
  totalAdvanceAmount: number;
}

export default function Dashboard() {
  const { user, profile, selectedCompany, userCompanies, signOut, loading } = useAuth();
  const { allowedModules, canViewModule } = useModulePermissions();
  const { stats, recentActivity, loading: statsLoading, refetch } = useDashboardStats();
  const navigate = useNavigate();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [showAccessDetails, setShowAccessDetails] = useState(false);

  useEffect(() => {
    // Update time every second
    const interval = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  // Show loading spinner only briefly
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Redirect if not authenticated
  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  // Demo data with larger numbers to showcase formatting - now using real data
  // const stats = useDashboardStats provides real data

  const { formatCurrency } = useTenantConfig();

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('es-GT', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('es-GT', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getGreeting = () => {
    const hour = currentTime.getHours();
    if (hour < 12) return 'Buenos días';
    if (hour < 18) return 'Buenas tardes';
    return 'Buenas noches';
  };

  const handleSignOut = async () => {
    await signOut();
  };

  // Get available quick actions based on user permissions
  const getAvailableActions = () => {
    const actions = [];

    // Ventas/POS
    if (canViewModule('sales_pos') || canViewModule('invoices')) {
      actions.push({
        title: 'Nueva Venta',
        description: 'Registrar nueva venta',
        icon: ShoppingCart,
        route: canViewModule('sales_pos') ? '/sales/pos' : '/sales',
        color: 'bg-primary/10 text-primary'
      });
    }

    // Inventario
    if (canViewModule('inventory_products') || canViewModule('inventory_stock')) {
      actions.push({
        title: 'Gestionar Inventario',
        description: 'Productos y stock',
        icon: Package,
        route: '/inventory/products',
        color: 'bg-status-info-bg text-status-info'
      });
    }

    // Usuarios (solo si tiene permisos de administración)
    if (canViewModule('administration') || canViewModule('roles_permissions')) {
      actions.push({
        title: 'Gestionar Usuarios',
        description: 'Administrar personal',
        icon: Users,
        route: '/admin/roles',
        color: 'bg-status-success-bg text-status-success'
      });
    }

    // Reportes
    if (canViewModule('reports') || canViewModule('sales_targets')) {
      actions.push({
        title: 'Reportes Financieros',
        description: 'Análisis y estadísticas',
        icon: FileBarChart,
        route: '/reports',
        color: 'bg-primary/10 text-primary'
      });
    }

    // Compras (si tiene acceso)
    if (canViewModule('inventory_purchase_orders')) {
      actions.push({
        title: 'Gestionar Compras',
        description: 'Órdenes y proveedores',
        icon: Store,
        route: '/purchases/orders',
        color: 'bg-status-warning-bg text-warning'
      });
    }

    // Configuración (solo administradores)
    if (canViewModule('administration') || canViewModule('system_modules')) {
      actions.push({
        title: 'Configuración',
        description: 'Ajustes del sistema',
        icon: Settings,
        route: '/admin',
        color: 'bg-muted text-muted-foreground'
      });
    }

    return actions.slice(0, 6); // Máximo 6 acciones
  };

  const quickActions = getAvailableActions();

  // Navigation handlers for quick actions
  const handleActionClick = (route: string) => {
    navigate(route);
  };


  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background/98 to-background/95 p-4 sm:p-6 lg:p-12">
      <div className="max-w-8xl mx-auto space-y-8 sm:space-y-12">
        {/* Header - Apple-style breathing room */}
        <div className="bg-card/60 backdrop-blur-xl rounded-3xl border border-border/20 p-8 sm:p-10 lg:p-12 shadow-apple-lg apple-card animate-fade-in">
          <div className="flex flex-col space-y-8 lg:flex-row lg:items-center lg:justify-between lg:space-y-0">
            <div className="space-y-3">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground leading-tight tracking-tight">
                {getGreeting()}, {profile?.full_name || user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'Usuario'}
              </h1>
              <p className="text-lg sm:text-xl text-muted-foreground font-medium">
                <span className="hidden sm:inline">{formatDate(currentTime)} • </span>
                <span className="text-primary font-semibold">{formatTime(currentTime)}</span>
              </p>
            </div>
              <div className="flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-y-0 sm:space-x-4 lg:space-x-8">
                <div className="bg-background/70 backdrop-blur-md rounded-2xl p-4 sm:p-6 border border-border/15 shadow-apple">
                  <div className="flex items-center space-x-3 sm:space-x-4">
                    <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-xl bg-primary/10 flex items-center justify-center ring-1 ring-primary/20">
                      <Building2 className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <span className="font-semibold text-foreground text-sm sm:text-base lg:text-lg truncate block">
                        {selectedCompany?.name || 'Sin empresa seleccionada'}
                      </span>
                      <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                        {userCompanies.length} empresa{userCompanies.length !== 1 ? 's' : ''} disponible{userCompanies.length !== 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={refetch}
                    disabled={statsLoading}
                    className="w-full sm:w-auto apple-button hover:scale-[0.98] active:scale-95 transition-all duration-200 bg-background/80 backdrop-blur-sm border-border/20 shadow-apple-sm"
                  >
                    <RefreshCw className={`h-4 w-4 mr-2 ${statsLoading ? 'animate-spin' : ''}`} />
                    <span className="text-sm">Actualizar</span>
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={handleSignOut} 
                    className="w-full sm:w-auto apple-button hover:scale-[0.98] active:scale-95 transition-all duration-200 bg-background/80 backdrop-blur-sm border-border/20 shadow-apple-sm"
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    <span className="text-sm font-medium">Cerrar Sesión</span>
                  </Button>
                </div>
              </div>
          </div>
        </div>

        {/* Financial Overview - Agrupación Apple-style */}
        <div className="space-y-8">
          <div className="flex items-center space-x-4">
            <div className="h-1 w-16 bg-gradient-to-r from-primary to-primary/60 rounded-full"></div>
            <h2 className="text-2xl font-bold text-foreground tracking-tight">Resumen Financiero</h2>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
            <div 
              className="apple-card hover:scale-[1.02] hover:shadow-apple-md transition-all duration-500 cursor-pointer group animate-fade-in" 
              onClick={() => canViewModule('invoices') && handleActionClick('/sales')}
              style={{ animationDelay: '0ms' }}
            >
              <KPICard
                title="Ventas Totales"
                value={stats.totalSales}
                variant="sales"
                icon={Wallet}
                valueType="currency"
                currency={selectedCompany?.currency || 'GTQ'}
                trend={{
                  value: `${formatPercentage(stats.salesGrowth)} desde el mes pasado`,
                  icon: TrendingUp,
                  positive: stats.salesGrowth > 0
                }}
              />
              {canViewModule('invoices') && (
                <div className="absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r from-success/30 to-success/50 group-hover:from-success/50 group-hover:to-success/70 transition-all duration-300 rounded-b-lg" />
              )}
            </div>

            <div 
              className="apple-card hover:scale-[1.02] hover:shadow-apple-md transition-all duration-500 cursor-pointer group animate-fade-in" 
              onClick={() => canViewModule('invoices') && handleActionClick('/sales')}
              style={{ animationDelay: '100ms' }}
            >
              <KPICard
                title="Pedidos"
                value={stats.totalOrders}
                variant="orders"
                icon={ShoppingCart}
                valueType="number" 
                trend={{
                  value: `${formatPercentage(stats.ordersGrowth)} desde el mes pasado`,
                  icon: TrendingUp,
                  positive: stats.ordersGrowth > 0
                }}
              />
              {canViewModule('invoices') && (
                <div className="absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r from-info/30 to-info/50 group-hover:from-info/50 group-hover:to-info/70 transition-all duration-300 rounded-b-lg" />
              )}
            </div>

            <div 
              className="apple-card hover:scale-[1.02] hover:shadow-apple-md transition-all duration-500 cursor-pointer group animate-fade-in" 
              onClick={() => canViewModule('inventory_products') && handleActionClick('/inventory/products')}
              style={{ animationDelay: '200ms' }}
            >
              <KPICard
                title="Productos"
                value={stats.totalProducts}
                variant="products"
                icon={Package}
                valueType="number"
                trend={{
                  value: `${stats.lowStock} con stock bajo`,
                  icon: AlertCircle,
                  positive: false
                }}
              />
              {canViewModule('inventory_products') && (
                <div className="absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r from-warning/30 to-warning/50 group-hover:from-warning/50 group-hover:to-warning/70 transition-all duration-300 rounded-b-lg" />
              )}
            </div>

            <div 
              className="apple-card hover:scale-[1.02] hover:shadow-apple-md transition-all duration-500 cursor-pointer group animate-fade-in" 
              onClick={() => canViewModule('administration') && handleActionClick('/admin')}
              style={{ animationDelay: '300ms' }}
            >
              <KPICard
                title="Usuarios Activos"
                value={`${stats.activeUsers}/${stats.totalUsers}`}
                variant="users"
                icon={Users}
                valueType="number"
                compactFormat={false}
                trend={{
                  value: `${formatPercentage(Math.round((stats.activeUsers / stats.totalUsers) * 100))} activos ahora`,
                  icon: Activity,
                  positive: true
                }}
              />
              {canViewModule('administration') && (
                <div className="absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r from-primary/30 to-primary/50 group-hover:from-primary/50 group-hover:to-primary/70 transition-all duration-300 rounded-b-lg" />
              )}
            </div>
          </div>
        </div>

        {/* Operational Metrics - Segunda sección agrupada */}
        <div className="space-y-8">
          <div className="flex items-center space-x-4">
            <div className="h-1 w-16 bg-gradient-to-r from-warning to-warning/60 rounded-full"></div>
            <h2 className="text-2xl font-bold text-foreground tracking-tight">Métricas Operacionales</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
            <div className="apple-card hover:scale-[1.02] hover:shadow-apple-md transition-all duration-500 animate-fade-in" style={{ animationDelay: '400ms' }}>
              <KPICard
                title="Órdenes Anticipo"
                value={stats.advanceOrders}
                variant="advance"
                icon={Clock}
                valueType="number"
                trend={{
                  value: `${stats.pendingAdvanceOrders} pendientes`,
                  icon: AlertCircle,
                  positive: stats.pendingAdvanceOrders === 0
                }}
              />
            </div>

            <div className="apple-card hover:scale-[1.02] hover:shadow-apple-md transition-all duration-500 animate-fade-in" style={{ animationDelay: '500ms' }}>
              <KPICard
                title="Valor Anticipos"
                value={stats.totalAdvanceAmount}
                variant="value"
                icon={Wallet}
                valueType="currency"
                currency={selectedCompany?.currency || 'GTQ'}
                subtitle="Órdenes con anticipo"
              />
            </div>

            <div className="apple-card hover:scale-[1.02] hover:shadow-apple-md transition-all duration-500 animate-fade-in" style={{ animationDelay: '600ms' }}>
              <KPICard
                title="Actividad Sistema"
                value={stats.activeUsers}
                variant="activity"
                icon={Activity}
                valueType="number"
                trend={{
                  value: "usuarios conectados",
                  icon: Users,
                  positive: true
                }}
              />
            </div>
          </div>
        </div>

        {/* Business Intelligence - Tercera sección agrupada */}
        <div className="space-y-8">
          <div className="flex items-center space-x-4">
            <div className="h-1 w-16 bg-gradient-to-r from-info to-info/60 rounded-full"></div>
            <h2 className="text-2xl font-bold text-foreground tracking-tight">Inteligencia de Negocio</h2>
          </div>
          
          <div className="animate-fade-in" style={{ animationDelay: '700ms' }}>
            <BusinessIntelligencePanel 
              showAccessDetails={showAccessDetails}
              onToggleAccessDetails={() => setShowAccessDetails(!showAccessDetails)}
            />
          </div>
        </div>

        {/* Real-Time Analytics - Cuarta sección agrupada */}
        <div className="space-y-8 animate-fade-in" style={{ animationDelay: '1100ms' }}>
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
            <div className="flex items-center space-x-4">
              <div className="h-1 w-16 bg-gradient-to-r from-success to-success/60 rounded-full"></div>
              <div>
                <h2 className="text-2xl font-bold text-foreground tracking-tight">Análisis en Tiempo Real</h2>
                <p className="text-base text-muted-foreground mt-1">Rendimiento y preferencias de clientes</p>
              </div>
            </div>
            <Badge className="bg-success/10 text-success border-success/20 px-4 py-2 text-sm font-medium shadow-apple-sm">
              <div className="w-2 h-2 bg-success rounded-full animate-pulse mr-2"></div>
              En vivo
            </Badge>
          </div>
          
          <div className="bg-card/40 backdrop-blur-xl rounded-3xl border border-border/20 p-8 shadow-apple-lg">
            <SalesIndicators />
          </div>
        </div>

        {/* Activity & Insights - Quinta sección agrupada */}
        <div className="space-y-8">
          <div className="flex items-center space-x-4">
            <div className="h-1 w-16 bg-gradient-to-r from-primary to-primary/60 rounded-full"></div>
            <h2 className="text-2xl font-bold text-foreground tracking-tight">Actividad e Insights</h2>
          </div>
          
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
            {/* Recent Activity */}
            <div className="xl:col-span-2 animate-fade-in" style={{ animationDelay: '1200ms' }}>
              <Card className="h-full apple-card bg-card/50 backdrop-blur-xl border-border/20 shadow-apple-lg">
                <CardHeader className="pb-6">
                  <CardTitle className="flex items-center text-xl font-bold">
                    <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center mr-4 ring-1 ring-primary/20">
                      <Clock className="h-5 w-5 text-primary" />
                    </div>
                    Actividad Reciente
                  </CardTitle>
                  <CardDescription className="text-base text-muted-foreground font-medium ml-14">
                    Últimas transacciones y eventos del sistema
                  </CardDescription>
                </CardHeader>
                <CardContent className="pl-14">
                  <div className="space-y-5">
                    {statsLoading ? (
                      <div className="space-y-5">
                        {[1, 2, 3, 4].map((i) => (
                          <div key={i} className="animate-pulse">
                            <div className="h-18 bg-muted/30 rounded-2xl"></div>
                          </div>
                        ))}
                      </div>
                    ) : recentActivity.length > 0 ? (
                      recentActivity.map((activity, index) => (
                        <div key={index} className="group flex items-center justify-between p-5 bg-background/50 backdrop-blur-md rounded-2xl border border-border/15 hover:bg-background/70 hover:scale-[1.01] hover:shadow-apple transition-all duration-400 shadow-apple-sm">
                          <div className="flex items-center space-x-4 min-w-0 flex-1">
                            <div className={`h-3 w-3 rounded-full flex-shrink-0 shadow-sm ${
                              activity.type === 'sale' ? 'bg-primary shadow-primary/20' :
                              activity.type === 'inventory' ? 'bg-amber-500 shadow-amber-500/20' :
                              activity.type === 'user' ? 'bg-blue-500 shadow-blue-500/20' : 'bg-red-500 shadow-red-500/20'
                            }`} />
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-semibold text-foreground truncate group-hover:text-primary transition-colors duration-200">{activity.description}</p>
                              <p className="text-xs text-muted-foreground truncate mt-1">por {activity.user}</p>
                            </div>
                          </div>
                          <Badge variant="outline" className="text-xs flex-shrink-0 ml-3 bg-background/80 border-border/30">
                            {activity.time}
                          </Badge>
                        </div>
                      ))
                    ) : (
                      <div className="text-center text-muted-foreground py-8">
                        <Clock className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">No hay actividad reciente</p>
                        <p className="text-xs">La actividad aparecerá aquí cuando ocurran eventos</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Quick Actions */}
            <div className="animate-fade-in" style={{ animationDelay: '1300ms' }}>
              <Card className="h-full apple-card bg-card/50 backdrop-blur-xl border-border/20 shadow-apple-lg">
                <CardHeader className="pb-6">
                  <CardTitle className="text-xl font-bold">Acciones Rápidas</CardTitle>
                  <CardDescription className="text-base text-muted-foreground font-medium">
                    Funciones más utilizadas
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {quickActions.map((action, index) => (
                    <Button 
                      key={index}
                      className="w-full justify-start text-sm apple-button hover:scale-[0.98] active:scale-95 transition-all duration-400 bg-background/60 backdrop-blur-sm border-border/20 shadow-apple-sm" 
                      variant="outline"
                      onClick={() => handleActionClick(action.route)}
                    >
                      <div className={`h-8 w-8 rounded-xl ${action.color} flex items-center justify-center mr-4`}>
                        <action.icon className="h-4 w-4" />
                      </div>
                      <div className="flex flex-col items-start">
                        <span className="truncate font-semibold">{action.title}</span>
                        <span className="text-xs text-muted-foreground truncate">{action.description}</span>
                      </div>
                    </Button>
                  ))}
                  
                  {quickActions.length === 0 && (
                    <div className="text-center text-muted-foreground py-8">
                      <UserCheck className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">No hay acciones disponibles</p>
                      <p className="text-xs">Contacta al administrador para obtener permisos</p>
                    </div>
                  )}
                </CardContent>
              </Card>
              
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}